import React from 'react';
import { Link } from 'react-router-dom';
import { X, Plus, Minus, Trash2, ShoppingBag } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';

export const CartDrawer = () => {
  const { 
    items, 
    itemCount, 
    subtotal, 
    total, 
    isOpen, 
    closeCart, 
    updateQuantity, 
    removeItem 
  } = useCart();

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-CH', {
      style: 'currency',
      currency: 'CHF'
    }).format(price);
  };

  return (
    <Sheet open={isOpen} onOpenChange={closeCart}>
      <SheetContent 
        side="right" 
        className="w-full sm:w-[420px] bg-warm-alabaster p-0 flex flex-col"
        data-testid="cart-drawer"
      >
        {/* Header */}
        <SheetHeader className="p-6 border-b border-border-subtle">
          <div className="flex items-center justify-between">
            <SheetTitle className="font-serif text-xl font-semibold text-espresso">
              Votre Panier ({itemCount})
            </SheetTitle>
          </div>
        </SheetHeader>

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full p-6 text-center">
              <div className="w-16 h-16 rounded-full bg-warm-secondary flex items-center justify-center mb-4">
                <ShoppingBag className="w-8 h-8 text-soft-charcoal-secondary" />
              </div>
              <h3 className="font-serif text-lg font-semibold text-espresso mb-2">
                Votre panier est vide
              </h3>
              <p className="text-soft-charcoal-secondary text-sm mb-6">
                Découvrez nos délicieux produits et ajoutez-les à votre panier.
              </p>
              <Button
                onClick={closeCart}
                asChild
                className="btn-primary"
                data-testid="browse-products-btn"
              >
                <Link to="/menu">Voir le Menu</Link>
              </Button>
            </div>
          ) : (
            <ul className="divide-y divide-border-subtle">
              {items.map((item) => (
                <li key={item.id} className="p-6" data-testid={`cart-item-${item.id}`}>
                  <div className="flex gap-4">
                    {/* Image */}
                    <div className="w-20 h-20 bg-warm-secondary rounded-sm overflow-hidden flex-shrink-0">
                      <img
                        src={item.image_url}
                        alt={item.name}
                        className="w-full h-full object-cover"
                      />
                    </div>

                    {/* Details */}
                    <div className="flex-1 min-w-0">
                      <h4 className="font-serif font-medium text-espresso truncate">
                        {item.name}
                      </h4>
                      <p className="text-latte-gold font-medium mt-1">
                        {formatPrice(item.price)}
                      </p>

                      {/* Quantity Controls */}
                      <div className="flex items-center justify-between mt-3">
                        <div className="flex items-center border border-border-subtle">
                          <button
                            onClick={() => updateQuantity(item.id, item.quantity - 1)}
                            data-testid={`decrease-qty-${item.id}`}
                            className="w-8 h-8 flex items-center justify-center hover:bg-warm-secondary transition-colors duration-300"
                          >
                            <Minus className="w-4 h-4" />
                          </button>
                          <span className="w-10 text-center text-sm font-medium">
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => updateQuantity(item.id, item.quantity + 1)}
                            data-testid={`increase-qty-${item.id}`}
                            className="w-8 h-8 flex items-center justify-center hover:bg-warm-secondary transition-colors duration-300"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>

                        <button
                          onClick={() => removeItem(item.id)}
                          data-testid={`remove-item-${item.id}`}
                          className="text-soft-charcoal-secondary hover:text-red-500 transition-colors duration-300"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="border-t border-border-subtle p-6 space-y-4 bg-warm-alabaster">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-soft-charcoal-secondary">Sous-total</span>
                <span className="text-soft-charcoal">{formatPrice(subtotal)}</span>
              </div>
              <Separator className="bg-border-subtle" />
              <div className="flex justify-between">
                <span className="font-serif font-semibold text-espresso">Total</span>
                <span className="font-serif font-semibold text-espresso">{formatPrice(total)}</span>
              </div>
            </div>

            <div className="space-y-3">
              <Button
                asChild
                className="w-full btn-primary"
                data-testid="checkout-btn"
                onClick={closeCart}
              >
                <Link to="/panier">Passer au Paiement</Link>
              </Button>
              <Button
                variant="outline"
                className="w-full btn-secondary"
                onClick={closeCart}
                data-testid="continue-shopping-btn"
              >
                Continuer mes Achats
              </Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartDrawer;
